/**
 * FleetManager V3 - Sites Widget
 * Visual sites distribution overview - clickable to navigate to sites view
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '../../shared/index.js';

// Site type configuration — aligned with YAML enum working_site.site_type
// (headquarters, branch, depot, parking, workshop, client_site, temporary).
// Legacy keys (siege, client, chantier) kept as aliases for older DB rows.
// Libellés : dashboard.sites.types.<type>
const SITE_TYPES = {
  headquarters: { color: 'bg-blue-500' },
  branch: { color: 'bg-cyan-500' },
  depot: { color: 'bg-purple-500' },
  parking: { color: 'bg-slate-500' },
  workshop: { color: 'bg-amber-500' },
  client_site: { color: 'bg-emerald-500' },
  temporary: { color: 'bg-orange-500' },
};

const LEGACY_TYPE_ALIAS = {
  siege: 'headquarters',
  client: 'client_site',
  chantier: 'temporary',
};

export function SitesWidget({ data = {}, onNavigate }) {
  const { t } = useTranslation();
  const {
    total = 0,
    byType = {}
  } = data;

  // Coalesce legacy DB keys onto the canonical YAML keys so old rows still render.
  const normalizedByType = Object.entries(byType).reduce((acc, [k, v]) => {
    const canonical = LEGACY_TYPE_ALIAS[k] || k;
    acc[canonical] = (acc[canonical] || 0) + v;
    return acc;
  }, {});

  // Build segments from canonical site types.
  const knownSegments = Object.entries(SITE_TYPES).map(([type, config]) => {
    const count = normalizedByType[type] || 0;
    return {
      type,
      label: t(`dashboard.sites.types.${type}`),
      value: count,
      color: config.color,
      percent: total > 0 ? (count / total) * 100 : 0
    };
  }).filter(seg => seg.value > 0);

  // Bucket anything we don't recognize so the total adds up (REG060).
  const knownTotal = knownSegments.reduce((sum, s) => sum + s.value, 0);
  const otherCount = Math.max(0, total - knownTotal);
  const segments = otherCount > 0
    ? [...knownSegments, {
        type: 'other',
        label: t('dashboard.sites.types.other'),
        value: otherCount,
        color: 'bg-gray-400',
        percent: total > 0 ? (otherCount / total) * 100 : 0,
      }]
    : knownSegments;

  // If no data, show placeholder
  if (total === 0) {
    segments.push(
      { label: t('dashboard.sites.types.none'), value: 0, color: 'bg-gray-300', percent: 100 }
    );
  }

  const handleClick = () => {
    onNavigate?.('sites');
  };

  return (
    <Card
      className="cursor-pointer hover:ring-1 hover:ring-blue-500/50 transition-all"
      onClick={handleClick}
    >
      <CardHeader>
        <CardTitle icon={MapPin} iconColor="text-purple-400">{t('dashboard.sites.title')}</CardTitle>
        <div className="flex items-center gap-1">
          <span className="text-lg font-bold text-gray-900 dark:text-white">{total}</span>
          <ChevronRight size={14} className="text-gray-400 dark:text-slate-500" />
        </div>
      </CardHeader>

      {/* Progress bar */}
      <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full flex overflow-hidden mb-3">
        {segments.map((seg, i) => (
          <div
            key={i}
            className={`${seg.color} transition-all`}
            style={{ width: `${seg.percent}%` }}
          />
        ))}
      </div>

      {/* Legend — adaptive grid up to 4 columns, all visible types shown */}
      <div className={`grid gap-2 text-center ${segments.length <= 2 ? 'grid-cols-2' : segments.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
        {segments.map((seg, i) => (
          <div key={i}>
            <div className="flex items-center justify-center gap-1">
              <div className={`w-2 h-2 rounded-full ${seg.color}`} />
              <span className="font-semibold text-gray-900 dark:text-white text-sm">{seg.value}</span>
            </div>
            <span className="text-xs text-gray-500 dark:text-slate-400">{seg.label}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default SitesWidget;
